// Deliverables must be explicitly named. The only legacy URL exception is a completed
// Higgsfield output, so saved creative sessions can recover their actual generated assets.
const LANGUAGES = Object.freeze({
  md: "markdown", markdown: "markdown", txt: "text", csv: "csv", json: "json",
  html: "html", css: "css", js: "javascript", jsx: "jsx", ts: "typescript", tsx: "tsx",
  py: "python", sql: "sql", xml: "xml", svg: "svg", yaml: "yaml", yml: "yaml", sh: "bash",
});
const FILE_TYPES = Object.freeze({
  png: "image", jpg: "image", jpeg: "image", webp: "image", gif: "image", avif: "image",
  mp4: "video", webm: "video", mov: "video", mp3: "audio", wav: "audio", ogg: "audio", m4a: "audio",
  pdf: "file", docx: "file", xlsx: "file", pptx: "file", zip: "file",
});

/** Only remote HTTPS assets, never machine paths, credentials, or local-network URLs.
 * No server-side fetch/proxy is involved; previews use the browser's media elements.
 */
export function safeArtifactUrl(value) {
  if (typeof value !== "string" || !value || value.length > 8192 || /[\s<>"`\\]/.test(value)) return null;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (url.protocol !== "https:" || url.username || url.password || url.hash || (url.port && url.port !== "443")) return null;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host) || /(?:^|\.)(?:localhost|local|internal|lan|home|test|invalid|example)$/.test(host)) return null;
    return url.href;
  } catch { return null; }
}

function artifactMetadata(info) {
  const match = /^artifact[ \t]+([A-Za-z0-9][A-Za-z0-9._ -]{0,159})$/.exec(info.trim());
  if (!match) return null;
  const title = match[1].trim();
  const extension = title.split(".").at(-1)?.toLowerCase();
  const language = Object.hasOwn(LANGUAGES, extension) ? LANGUAGES[extension] : null;
  if (!title.includes(".") || title.includes("..")) return null;
  if (language) return { title, kind: language, language };
  return Object.hasOwn(FILE_TYPES, extension) ? { title, kind: FILE_TYPES[extension], extension } : null;
}

function remoteArtifact(metadata, content) {
  const url = safeArtifactUrl(content.trim());
  if (!url) return null;
  // A signed, extensionless download URL is allowed only in an explicit named block.
  let filename;
  try { filename = decodeURIComponent(new URL(url).pathname.split("/").at(-1)); } catch { return null; }
  const actualExtension = filename.includes(".") ? filename.split(".").at(-1).toLowerCase() : null;
  if (actualExtension !== null && actualExtension !== metadata.extension && ![actualExtension, metadata.extension].every((ext) => ["jpg", "jpeg"].includes(ext))) return null;
  return { ...metadata, url };
}

function legacyHiggsfieldAsset(line, heading) {
  const linked = /^\s*!?\[([^\]\n]+)\]\((https:\/\/[^\s)]+)\)\s*$/.exec(line);
  const bare = /^\s*(https:\/\/[^\s<>]+)\s*$/.exec(line);
  const url = safeArtifactUrl(linked?.[2] || bare?.[1]);
  if (!url) return null;
  const parsed = new URL(url);
  // This is the generated-file path used by Higgsfield, not a generic image link.
  const file = /^\/user_[A-Za-z0-9]+\/hf_\d{8}_\d{6}_[a-f0-9-]{36}\.(png|jpg|jpeg|webp|gif|avif|mp4|webm|mov)$/i.exec(parsed.pathname);
  if (parsed.hostname !== "d8j0ntlcm91z4.cloudfront.net" || !file) return null;
  const extension = file[1].toLowerCase();
  const label = (linked?.[1] || heading || "Generated creative").replace(/[*_`]/g, "").trim().slice(0, 120)
    .replace(new RegExp(`\\.${extension}$`, "i"), "").replace(/[. ]+$/, "");
  return { title: `${label || "Generated creative"}.${extension}`, kind: FILE_TYPES[extension], extension, url };
}

/** Parse deliverables consistently from completed streams and saved history.
 * Text artifacts contain their actual content; remote assets retain the service URL.
 * The viewer never executes HTML/scripts or invents external file bytes.
 */
export function parseArtifactMessage(value) {
  const text = typeof value === "string" ? value : "";
  const lines = text.split(/\r?\n/);
  const artifacts = [];
  const display = [];
  let fence = null;
  let cursor = 0;
  let heading = "";
  const seenUrls = new Set();
  lines.forEach((line, index) => {
    const match = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);
    if (!match) {
      if (fence) return;
      const label = /^\s*(?:\d+[.)]|[-*]|#{1,6})\s+["“]([^"”]+)["”]/.exec(line);
      // Only the nearby quoted creative title is used; ordinary prose is not a filename.
      if (line.trim()) heading = label?.[1] || (line.trim().startsWith("https://") || line.trim().startsWith("[") || line.trim().startsWith("![") ? heading : "");
      const asset = legacyHiggsfieldAsset(line, heading);
      if (asset) {
        if (!seenUrls.has(asset.url)) {
          seenUrls.add(asset.url);
          artifacts.push({ ...asset, index: artifacts.length });
        }
        display.push(...lines.slice(cursor, index), `[${asset.title}](${asset.url})`);
        cursor = index + 1;
        heading = "";
      }
      return;
    }
    if (!fence) {
      fence = { marker: match[1][0], length: match[1].length, start: index, metadata: artifactMetadata(match[2]) };
      return;
    }
    // Ignore nested fences, examples inside ordinary code blocks, and partial closes.
    if (match[1][0] !== fence.marker || match[1].length < fence.length || match[2].trim()) return;
    const content = lines.slice(fence.start + 1, index).join("\n");
    const artifact = fence.metadata && content.trim() && (fence.metadata.extension
      ? remoteArtifact(fence.metadata, content)
      : { ...fence.metadata, content });
    if (artifact) {
      if (!artifact.url || !seenUrls.has(artifact.url)) {
        artifacts.push({ ...artifact, index: artifacts.length });
        if (artifact.url) seenUrls.add(artifact.url);
      }
      display.push(...lines.slice(cursor, fence.start), `Created **${fence.metadata.title}**.`);
      cursor = index + 1;
    }
    fence = null;
  });
  return { text: artifacts.length ? [...display, ...lines.slice(cursor)].join("\n") : text, artifacts };
}
